import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, PlusCircle } from 'lucide-react';
import { Button } from './button';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { potentialMatchNames, selectVerificationOutcome, verificationOutcomeLabel } from '@/domain/document-processing/utils/verification-outcome';

export function OrderHeader({ orderNumber, manualOrderNumber, onNewOrder, exportRef }: {
  orderNumber: string;
  manualOrderNumber?: string;
  onNewOrder: () => void;
  exportRef: React.Ref<HTMLDivElement>;
}) {
  const state = useDocumentStore();
  const outcome = selectVerificationOutcome(state);
  const matchCount = potentialMatchNames(state).length;
  const Icon = outcome === 'running' ? Clock : outcome === 'complete' ? CheckCircle2 : AlertTriangle;

  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-2">
        <h2 className="text-xl font-semibold tracking-tight break-words">
          Order {orderNumber}
          {manualOrderNumber && <span className="ml-2 text-sm font-normal text-muted-foreground">PO #{manualOrderNumber}</span>}
        </h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="inline-flex items-center gap-1.5">
            <Icon aria-hidden="true" className="h-4 w-4" />
            {verificationOutcomeLabel[outcome]}
          </span>
          {outcome === 'complete' && matchCount > 0 && <span className="text-destructive">
            {matchCount} potential {matchCount === 1 ? 'match' : 'matches'}
          </span>}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div ref={exportRef} />
          <Button onClick={onNewOrder} disabled={state.isProcessing} variant="outline" size="sm" className="gap-1.5"
            title={state.isProcessing ? 'Wait for verification to finish before starting a new order.' : undefined}>
            <PlusCircle aria-hidden="true" className="h-4 w-4" />
            New Order
          </Button>
        </div>
        {state.isProcessing && <p className="text-xs text-muted-foreground">New Order is available when verification finishes.</p>}
      </div>
    </header>
  );
}
